import { useAuthApiStore } from './authApiStore'

import { createClient } from '@/v31-buyer/client/client.gen'

import { createLog } from '@/src/lib/logger'

// Dev fetch interception (delay/error simulation)
import {
  devFetchDelayEnabled,
  devFetchDelayValue,
  devFetchErrorCode,
} from '@/src/components/shared/devFetchInterception'

const log = createLog('clientInterceptor')

function setupDevInterceptors(client: ReturnType<typeof createClient>) {
  // Dev-only: Add delay to requests
  client.interceptors.request.use(async (request: Request) => {
    if (devFetchDelayEnabled.value) {
      await new Promise((res) =>
        setTimeout(res, devFetchDelayValue.value * 1000)
      )
    }
    return request
  })

  // Dev-only: Simulate HTTP errors in response interceptor
  client.interceptors.response.use((response: Response) => {
    if (devFetchErrorCode.value && devFetchErrorCode.value !== '') {
      const errorCode = devFetchErrorCode.value as number

      // Create appropriate error message based on status code
      let errorMessage = ''
      switch (errorCode) {
        case 401:
          errorMessage = 'Simulated 401: Unauthorized - Authentication required'
          break
        case 403:
          errorMessage = 'Simulated 403: Forbidden - Access denied'
          break
        case 500:
          errorMessage =
            'Simulated 500: Internal Server Error - Something went wrong'
          break
        default:
          errorMessage = `Simulated ${errorCode}: HTTP Error`
      }

      const errorBody = JSON.stringify({
        error: 'simulated error',
        description: errorMessage,
        status: errorCode,
        timestamp: new Date().toISOString(),
        simulated: true,
      })

      // Create a new response with the simulated error status and message
      return new Response(errorBody, {
        status: errorCode,
        statusText: `Simulated ${errorCode}`,
        headers: {
          ...response.headers,
          'Content-Type': 'application/json',
        },
      })
    }
    return response
  })
}

export function setupAuthInterceptor(client: ReturnType<typeof createClient>) {
  // Set up dev interceptors only in development mode
  if (import.meta.env.DEV) {
    setupDevInterceptors(client)
  }

  client.interceptors.request.use(async (request: Request) => {
    log(request)
    const authStore = useAuthApiStore()

    await authStore.checkTokenExpiry()
    const token = authStore.accessToken

    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`)
    }

    return request
  })

  client.interceptors.response.use((response: Response) => {
    return response
  })

  client.interceptors.error.use(async (error: unknown, response: Response) => {
    // Handle HTTP errors
    if (response?.status === 401) {
      const authStore = useAuthApiStore()
      await authStore.logout()
      return response
    }

    if (response?.status === 403) {
      log(error, response)
      throw error
    }

    // normally a 500, but deal with any non-2xx status code the same
    log(error, response)
    throw error
  })
}
